import { Fragment, useState } from 'react';
import * as RadixSelect from '@radix-ui/react-select';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Building2, Check, ChevronDown, Code2, ExternalLink, FilePlus2, Files, Gauge, LogOut, Menu as MenuIcon, MoreHorizontal, Settings, UserCog, type LucideIcon } from 'lucide-react';
import { Menu, MenuContent, MenuItem, MenuTrigger } from './ui/menu';
import { Tooltip } from './ui/tooltip';
import { Pill } from './ui/pill';
import { usePageTitleOverride } from './page-title';
import { TourProvider } from '@/tour/TourProvider';
import { authStore } from '@/lib/auth-store';
import { leaveAfterSignOut, portalHomeUrl } from '@/lib/session-exit';
import { hasPortalAccount } from '@/lib/portal-account';
import { announceSessionEnded } from '@/lib/use-session-timeout';
import { useAuth } from '@/lib/use-auth';
import { draftUploadsQuery, hasCustomer, homePath, isStaff, meQuery } from '@/lib/queries';
import { useTaxYear } from '@/lib/tax-year';
import { modeCopy } from '@/lib/mode';
import { initialsOf } from '@/lib/format';
import { cn } from '@/lib/cn';
import { UploadsTray } from '@/components/uploads-tray';
import { ActivityAnnouncer, ActivityBar } from '@/components/ui/activity-bar';
import { LOGO_URL } from '@/lib/asset';

type AppRoute = '/e-files' | '/e-files/new' | '/settings' | '/developers' | '/admin' | '/admin/staff' | '/admin/usage';

/**
 * One view of a screen, listed under it.
 *
 * The portal puts a screen's views in the menu under the screen itself, indented and
 * bulleted, rather than in a strip along the top of the page. Same idea here: `param` is the
 * search key that chooses the view, and a child with no `value` is the one you land on.
 */
interface NavChild {
  label: string;
  to: AppRoute;
  param?: { key: string; value?: string };
}

interface NavItem {
  to: AppRoute;
  label: string;
  icon: LucideIcon;
  tour: string;
  children?: NavChild[];
}

interface NavGroup {
  /** Small grey heading over the group. The first group has none. */
  label?: string;
  divided?: boolean;
  items: NavItem[];
}

const customerGroups: NavGroup[] = [
  {
    items: [
      {
        to: '/e-files',
        label: 'E-files',
        icon: Files,
        tour: 'nav-e-files',
        children: [
          { label: 'All e-files', to: '/e-files', param: { key: 'view' } },
          { label: 'Drafts', to: '/e-files', param: { key: 'view', value: 'drafts' } },
        ],
      },
      { to: '/e-files/new', label: 'New e-file', icon: FilePlus2, tour: 'nav-new-e-file' },
    ],
  },
  {
    label: 'Account',
    items: [
      {
        to: '/settings',
        label: 'Settings',
        icon: Settings,
        tour: 'nav-settings',
        children: [
          { label: 'Keys', to: '/settings', param: { key: 'tab' } },
          { label: 'Webhooks', to: '/settings', param: { key: 'tab', value: 'webhooks' } },
          { label: 'Users', to: '/settings', param: { key: 'tab', value: 'users' } },
        ],
      },
      {
        to: '/developers',
        label: 'Developers',
        icon: Code2,
        tour: 'nav-developers',
        children: [
          { label: 'Guide', to: '/developers', param: { key: 'view' } },
          { label: 'Endpoint reference', to: '/developers', param: { key: 'view', value: 'reference' } },
        ],
      },
    ],
  },
];

const staffGroup: NavGroup = {
  label: 'Tab Service',
  divided: true,
  items: [
    { to: '/admin', label: 'Customers', icon: Building2, tour: 'nav-admin' },
    { to: '/admin/staff', label: 'Staff', icon: UserCog, tour: 'nav-admin-staff' },
    { to: '/admin/usage', label: 'Usage', icon: Gauge, tour: 'nav-admin-usage' },
  ],
};

const roleLabel: Record<string, string> = {
  owner: 'Owner',
  member: 'Member',
  staff: 'Tab Service staff',
};

function isActive(pathname: string, to: string): boolean {
  if (to === '/e-files') return pathname === '/e-files' || /^\/e-files\/(?!new)/.test(pathname);
  if (to === '/admin') return pathname === '/admin' || pathname.startsWith('/admin/customers');
  return pathname === to || pathname.startsWith(`${to}/`);
}

type CrumbTo = '/e-files' | '/admin';

interface Crumb {
  label: string;
  to?: CrumbTo;
}

/**
 * The header says where you are, not just what the page is called. The last
 * crumb is the page; anything before it is the section you came through.
 */
function crumbsFor(pathname: string, override: string | null): Crumb[] {
  if (pathname.startsWith('/e-files/new')) return [{ label: 'E-files', to: '/e-files' }, { label: 'New e-file' }];
  if (/^\/e-files\/.+/.test(pathname)) return [{ label: 'E-files', to: '/e-files' }, { label: override ?? 'E-file' }];
  if (pathname.startsWith('/e-files')) return [{ label: 'E-files' }];
  if (pathname.startsWith('/settings')) return [{ label: 'Settings' }];
  if (pathname.startsWith('/developers')) return [{ label: 'Developers' }];
  if (pathname.startsWith('/admin/usage')) return [{ label: 'Tab Service' }, { label: 'Usage' }];
  if (pathname.startsWith('/admin/staff')) return [{ label: 'Tab Service' }, { label: 'Staff' }];
  if (pathname.startsWith('/admin/customers')) {
    return [{ label: 'Tab Service' }, { label: 'Customers', to: '/admin' }, { label: override ?? 'Customer' }];
  }
  if (pathname.startsWith('/admin')) return [{ label: 'Tab Service' }, { label: 'Customers' }];
  return [{ label: 'Tab Service' }];
}

export function AppShell() {
  const auth = useAuth();
  const { data: me } = useQuery(meQuery());
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const searchString = useRouterState({ select: (state) => state.location.searchStr });
  const override = usePageTitleOverride();
  const staff = isStaff(me);
  const customer = hasCustomer(me);
  const home = homePath(me);

  /*
    Two separate things, the way the portal keeps them. On a narrow screen the menu is a
    drawer that slides over the page, opened by the hamburger in the header. On a wide screen
    it is always there, and the control inside it narrows it to a rail of icons rather than
    taking it away - so there is always something to click to bring it back.
  */
  const [navOpen, setNavOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);

  const email = auth.user?.email ?? '';
  const role = me?.auth.user?.role;

  return (
    <TourProvider>
      <ActivityBar />
      <ActivityAnnouncer />
      <div className="flex min-h-screen bg-surface">
        {navOpen ? (
          <button
            type="button"
            aria-label="Close the menu"
            onClick={() => setNavOpen(false)}
            className="anim-fade-in fixed inset-0 z-30 bg-black/30 lg:hidden"
          />
        ) : null}

        <Sidebar
          open={navOpen}
          collapsed={navCollapsed}
          staff={staff}
          customer={customer}
          home={home}
          pathname={pathname}
          search={searchString}
          email={email}
          role={role}
          onToggle={() => setNavCollapsed((value) => !value)}
          onNavigate={() => {
            if (window.innerWidth < 1024) setNavOpen(false);
          }}
        />

        <div className="flex min-w-0 flex-1 flex-col">
          <Header
            crumbs={crumbsFor(pathname, override)}
            mode={customer ? me?.mode : undefined}
            customerName={me?.customer?.name}
            navOpen={navOpen}
            onToggleNav={() => setNavOpen((open) => !open)}
          />
          <main className="flex-1 bg-[image:linear-gradient(to_bottom,var(--ts-ground-top),var(--ts-ground-bottom))]">
            {/*
              Full width. A wide monitor is bought to be used, and capping the page centres it
              between two empty margins while the tables inside it go on wrapping. What made a
              wide screen look wrong was a header pinned to both edges at once, and that is
              fixed where it was: the controls sit on the left with the page.
            */}
            <div key={pathname} className="anim-route-in flex w-full flex-col gap-6 p-6">
              <Outlet />
            </div>
          </main>
        </div>

      </div>
    </TourProvider>
  );
}

function Sidebar({
  open,
  collapsed,
  staff,
  customer,
  home,
  pathname,
  search,
  email,
  role,
  onNavigate,
  onToggle,
}: {
  /** The drawer on a narrow screen. On a wide screen the menu is always there. */
  open: boolean;
  /** Narrowed to a rail of icons. Wide screens only; the drawer is never a rail. */
  collapsed: boolean;
  staff: boolean;
  customer: boolean;
  home: '/e-files' | '/admin';
  pathname: string;
  search: string;
  email: string;
  role: string | undefined;
  onNavigate: () => void;
  onToggle: () => void;
}) {
  // Customer screens only appear for identities that have a customer; staff always get theirs.
  const groups = [...(customer ? customerGroups : []), ...(staff ? [staffGroup] : [])];
  const navigate = useNavigate();
  /*
    Which screens are showing their views. The screen you are on always shows its own; the
    rest are opened and closed by hand, and stay however they were left while you move
    around, the way the portal's menu behaves.
  */
  const [opened, setOpened] = useState<Record<string, boolean>>({});
  const toggle = (to: string) => setOpened((current) => ({ ...current, [to]: !isOpen(to) }));
  const isOpen = (to: string) => opened[to] ?? isActive(pathname, to);
  const signOutFromMenu = () => signOut(navigate);
  // Read once per render rather than held in state: it is set before this shell ever mounts,
  // by the page that took the ticket from the portal.
  const portalLink = hasPortalAccount() ? portalHomeUrl() : null;

  return (
    <nav
      aria-label="Main"
      className={cn(
        'fixed inset-y-0 left-0 z-40 flex shrink-0 flex-col overflow-hidden border-r',
        'border-[color:var(--ts-border-soft)] bg-surface',
        'transition-[width,transform] duration-200 ease-out',
        'lg:sticky lg:top-0 lg:h-screen lg:visible lg:translate-x-0',
        // The drawer: full width, over the page, and out of reach when it is shut.
        open ? 'w-[263px] translate-x-0' : 'invisible w-[263px] -translate-x-full',
        // The rail: 74px of icons, the portal's own collapsed width.
        collapsed ? 'lg:w-[74px]' : 'lg:w-[263px]',
      )}
    >
      {/*
        The portal's own header row: 74px tall, the logo on the left and the collapse control
        on the right, inside the menu rather than out in the page header. The plate is white in
        both themes, because the mark is navy on white.
      */}
      <div
        className={cn(
          'flex h-[74px] w-[263px] shrink-0 items-center px-5',
          collapsed ? 'lg:w-[74px] lg:justify-center lg:px-0' : 'justify-between',
        )}
      >
        <Link
          to={home}
          aria-label="Tab Service home"
          data-tour="product"
          className={cn('inline-flex rounded-lg bg-white p-2', collapsed && 'lg:hidden')}
        >
          <img
            src={LOGO_URL}
            alt="TAB Service Company"
            width={104}
            height={32}
            className="h-8 w-auto"
          />
        </Link>
        <button
          type="button"
          aria-label={collapsed ? 'Widen the menu' : 'Narrow the menu'}
          aria-expanded={!collapsed}
          onClick={onToggle}
          className={cn(
            'hidden h-9 w-9 shrink-0 items-center justify-center rounded-full text-primary lg:inline-flex',
            'transition-colors duration-150 ease-out hover:bg-[color:var(--ts-row-hover)]',
            'focus-visible:shadow-[inset_0_0_0_2px_var(--ts-focus-ring)] focus-visible:outline-none',
          )}
        >
          <MenuIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
        </button>
      </div>

      <div
        className={cn(
          'flex w-[263px] flex-1 shrink-0 flex-col overflow-x-hidden overflow-y-auto pb-4',
          collapsed && 'lg:w-[74px]',
        )}
      >
        {groups.map((group, groupIndex) => (
          <Fragment key={group.label ?? `group-${groupIndex}`}>
            {group.divided ? (
              <hr className="my-2 border-0 border-t border-[color:var(--ts-border-soft)]" />
            ) : null}
            {/*
              No uppercase section heading. The portal's menu is one flat list, and a hairline
              is enough to say that Settings and Developers are a different kind of thing from
              the screens above them.
            */}
            {group.items.map((item) => (
              <NavRow
                key={item.to}
                item={item}
                collapsed={collapsed}
                pathname={pathname}
                search={search}
                open={isOpen(item.to)}
                onToggle={() => toggle(item.to)}
                onNavigate={onNavigate}
              />
            ))}
          </Fragment>
        ))}
      </div>

      {/*
        Who is signed in, at the foot of the menu, the way the Tab Service portal shows it,
        and the only place identity lives: the role you are signed in as, how the app looks,
        the guided tours, and the way out. There is no second copy of any of it in the page
        header.
      */}
      {email ? (
        <div
          className={cn(
            'w-[263px] shrink-0 border-t border-[color:var(--ts-border-soft)] p-4',
            collapsed && 'lg:w-[74px] lg:flex lg:justify-center lg:px-0',
          )}
        >
          {/*
            The whole footer is the control, as it is in the portal: avatar, who you are, what
            you are, and a three-dot at the end that opens the account menu.
          */}
          <Menu>
            <MenuTrigger
              data-tour="help"
              aria-label={`Account menu for ${email}`}
              className={cn(
                'flex w-full cursor-pointer items-center gap-2 rounded-lg px-1 py-1 text-left',
                'transition-colors duration-150 ease-out hover:bg-[color:var(--ts-row-hover)]',
                collapsed && 'lg:w-auto lg:justify-center lg:gap-0',
                'focus-visible:shadow-[inset_0_0_0_2px_var(--ts-focus-ring)] focus-visible:outline-none',
              )}
            >
              <span
                aria-hidden="true"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[color:var(--ts-primary-light)] text-sm font-medium text-primary"
              >
                {initialsOf(email)}
              </span>
              {/*
                The address, not a name made out of it. Tab Service has no display name, and
                guessing one from owner@northwindpayroll.com produces "Owner" - which is also
                what the line underneath says. The address is what somebody recognises as
                theirs and what support asks for when two people share a screen.
              */}
              <span className={cn('min-w-0 flex-1', collapsed && 'lg:hidden')}>
                <span className="block truncate text-sm font-medium text-primary" title={email}>
                  {email}
                </span>
                <span className="block truncate text-xs font-light text-muted-foreground">
                  {role ? (roleLabel[role] ?? role) : 'Signed in'}
                </span>
              </span>
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground',
                  collapsed && 'lg:hidden',
                )}
              >
                <MoreHorizontal className="h-5 w-5" />
              </span>
            </MenuTrigger>
            {/*
              Who you are and the way out. The guided tours and the appearance switch were
              here and are not offered anywhere now: both are still built, and putting either
              back is a line in this menu.
            */}
            <MenuContent align="start" side="top" className="min-w-[240px]">
              {/*
                Only for somebody who has a portal account as well, and only a way back to it:
                the portal decides what they may do when they arrive. Nothing is granted here.
              */}
              {portalLink ? (
                <MenuItem onSelect={() => window.location.assign(portalLink)}>
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  Tab Service portal
                </MenuItem>
              ) : null}
              <MenuItem onSelect={() => signOutFromMenu()}>
                <LogOut className="h-4 w-4" aria-hidden="true" />
                Sign out
              </MenuItem>
            </MenuContent>
          </Menu>
        </div>
      ) : null}
    </nav>
  );
}

/**
 * One screen in the menu, with its views under it.
 *
 * Drawn the way the Tab Service portal draws its own: the screen you are on is a light blue
 * row with a navy bar down its left edge and its icon filled in, and its views sit under it,
 * indented and bulleted, with the one you are looking at marked the same way. A screen with
 * no views is the same row without the chevron and without the list.
 *
 * The views only appear under the screen you are on. Every view of every screen, open at
 * once, is a menu somebody has to read rather than glance at.
 */
/**
 * The name of a row, shown on hover while the menu is a rail. The rail has no room for
 * labels, and an icon on its own is a guess until you have learnt it.
 */
function RailTip({ show, label, children }: { show: boolean; label: string; children: React.ReactNode }) {
  if (!show) return <>{children}</>;
  return (
    <Tooltip content={label} side="right">
      {children}
    </Tooltip>
  );
}

function NavRow({
  item,
  collapsed,
  pathname,
  search,
  open,
  onToggle,
  onNavigate,
}: {
  item: NavItem;
  /**
   * The menu is a rail of icons. Only ever true on a wide screen, so the label, the chevron
   * and the views are taken away with `lg:` rather than left out of the markup: the same row
   * is still the drawer's full-width row on a narrow screen.
   */
  collapsed: boolean;
  pathname: string;
  search: string;
  /** Whether this screen's views are showing. Open by itself for the screen you are on. */
  open: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  const Icon = item.icon;
  const active = isActive(pathname, item.to);
  const children = item.children ?? [];
  const hasChildren = children.length > 0;
  const listId = `nav-views-${item.to.replace(/\//g, '-')}`;

  return (
    <>
      {/*
        The row is a link and, when the screen has views, a separate control for showing
        them. Two controls rather than one, because a chevron nested inside a link is not
        valid markup and cannot be reached on its own by keyboard: this way the label goes
        to the screen and the chevron opens the list without going anywhere.
      */}
      <div
        className={cn(
          // An inset rounded row, the way the portal draws it: the fill stops short of both
          // edges of the menu and the navy bar sits on the row itself rather than on the
          // edge of the screen.
          'group/nav relative mx-2 flex h-12 items-center overflow-hidden rounded-lg',
          'transition-colors duration-150 ease-out',
          collapsed && 'lg:mx-[17px] lg:my-1 lg:h-10 lg:w-10 lg:justify-center',
          active
            ? 'bg-[color:var(--ts-primary-light)]'
            : 'hover:bg-[color:var(--ts-row-hover)]',
        )}
      >
        {active ? (
          <span
            aria-hidden="true"
            className={cn(
              'absolute inset-y-0 left-0 w-[4px] rounded-r-sm bg-primary',
              // A stripe down a 40px chip is all stripe. The fill says which one it is.
              collapsed && 'lg:hidden',
            )}
          />
        ) : null}
        <RailTip show={collapsed} label={item.label}>
          <Link
            to={item.to}
            data-tour={item.tour}
            onClick={() => {
              if (hasChildren && !open) onToggle();
              onNavigate();
            }}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'flex h-full min-w-0 flex-1 cursor-pointer items-center gap-2 pl-3',
              hasChildren ? 'pr-1' : 'pr-3',
              collapsed && 'lg:justify-center lg:gap-0 lg:px-0',
              'focus-visible:shadow-[inset_0_0_0_2px_var(--ts-focus-ring)] focus-visible:outline-none',
            )}
          >
            <span className={cn('flex w-8 shrink-0 justify-center', collapsed && 'lg:w-full')}>
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full',
                  'transition-colors duration-150 ease-out',
                  active ? 'bg-primary text-primary-foreground' : 'text-primary',
                  // On the rail the chip is the fill, so the icon is navy on light blue
                  // rather than white in a navy circle inside a light blue square.
                  collapsed && 'lg:bg-transparent lg:text-primary',
                )}
              >
                <Icon
                  className={cn('h-[18px] w-[18px]', collapsed && 'lg:h-5 lg:w-5')}
                  strokeWidth={1.75}
                />
              </span>
            </span>
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-sm',
                active ? 'font-medium text-primary' : 'text-gray-2',
                collapsed && 'lg:hidden',
              )}
            >
              {item.label}
            </span>
          </Link>
        </RailTip>
        {hasChildren ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-controls={listId}
            aria-label={`${open ? 'Hide' : 'Show'} the views of ${item.label}`}
            className={cn(
              'mr-1 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md',
              'transition-colors duration-150 ease-out hover:bg-[color:var(--ts-row-hover)]',
              collapsed && 'lg:hidden',
              'focus-visible:shadow-[inset_0_0_0_2px_var(--ts-focus-ring)] focus-visible:outline-none',
            )}
          >
            <ChevronDown
              aria-hidden="true"
              className={cn(
                'h-4 w-4 transition-transform duration-200 ease-out motion-reduce:transition-none',
                active ? 'text-primary' : 'text-gray-1',
                // Down when the views are closed, flipped when they are open, as the portal
                // draws it. A chevron pointing sideways reads as "goes somewhere else".
                open ? 'rotate-180' : 'rotate-0',
              )}
            />
          </button>
        ) : null}
      </div>

      {hasChildren ? (
        /*
          The views roll open and shut rather than blinking in and out. The two row
          template is what makes that possible without measuring anything: a grid row of
          0fr has no height, one of 1fr is exactly as tall as its content, and the step
          between them animates. Everything stays mounted, so closing is as smooth as
          opening, and `hidden` keeps the closed rows out of the tab order and away from a
          screen reader while they are not there to be read.
        */
        <div
          id={listId}
          className={cn(
            'grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
            open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
            // A rail has no room for the views, and its rows carry their name in a tooltip.
            collapsed && 'lg:hidden',
          )}
        >
          <div className="overflow-hidden" aria-hidden={open ? undefined : true} {...(open ? {} : { inert: '' })}>
            {children.map((child) => (
              <NavChildRow
                key={`${child.to}-${child.param?.value ?? 'default'}`}
                child={child}
                pathname={pathname}
                search={search}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

/** A view of the screen above it: a label on a guide line, which goes navy for the one showing. */
function NavChildRow({
  child,
  pathname,
  search,
  onNavigate,
}: {
  child: NavChild;
  pathname: string;
  search: string;
  onNavigate: () => void;
}) {
  const { taxYear } = useTaxYear();
  // Only the drafts row counts drafts, and only while the e-file screen is the one open.
  const counts = child.param?.value === 'drafts';
  const drafts = useQuery({ ...draftUploadsQuery(taxYear), enabled: counts && pathname === '/e-files' });
  // The badge is the total the API reports, not the length of the page that came back:
  // the query asks for one page, so counting its rows capped the badge at the page size
  // and read 10 for 23 drafts (TS-8444).
  const count = counts ? (drafts.data?.total ?? drafts.data?.items?.length ?? 0) : 0;

  const params = new URLSearchParams(search);
  const chosen = child.param ? (params.get(child.param.key) ?? undefined) : undefined;
  // A view with no value of its own is the one you land on, so an absent parameter is it.
  const active = pathname === child.to && chosen === child.param?.value;

  return (
    <Link
      to={child.to}
      search={(old) => ({ ...old, [child.param?.key ?? '']: child.param?.value })}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        // A view of the screen above it, and it has to read as one at a glance. Drawn the way
        // the screen is - full-width fill, rounded the same, a navy bar down the left edge -
        // a chosen view looks like a second screen, and the menu stops having a shape.
        //
        // So the row itself is quieter in every dimension: shorter, inset further than the
        // screen's own row, a smaller radius, smaller type. The label still starts at 52px,
        // under the screen's own label, because these are its views and not another list.
        'group/child relative mx-3 flex h-8 cursor-pointer items-center rounded-[6px] pr-3 pl-[40px]',
        'transition-colors duration-150 ease-out',
        'focus-visible:shadow-[inset_0_0_0_2px_var(--ts-focus-ring)] focus-visible:outline-none',
        // Chosen, and visibly so, at a third of the strength of the screen's own fill.
        active ? 'bg-[color:var(--ts-primary-light)]/45' : 'hover:bg-[color:var(--ts-row-hover)]',
      )}
    >
      {/*
        The guide the views hang from: a hairline under the middle of the screen's icon,
        running the height of every row so the group reads as one list, and turning navy and
        thick for exactly the row you are on. This is what marks the chosen view - a bar on
        the outside edge is the screen's own mark, and using it twice made the two rows argue
        about which of them you were looking at.
      */}
      <span aria-hidden="true" className="absolute inset-y-0 left-[15px] w-px bg-[color:var(--ts-border)]" />
      {active ? (
        <span aria-hidden="true" className="absolute left-[14px] inset-y-[5px] w-[3px] rounded-full bg-primary" />
      ) : null}
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-[13px]',
          active ? 'font-medium text-primary' : 'text-gray-1 group-hover/child:text-foreground',
        )}
      >
        {child.label}
      </span>
      {count > 0 ? (
        <span className="shrink-0 text-[11px] font-medium text-muted-foreground tabular-nums">{count}</span>
      ) : null}
    </Link>
  );
}

/**
 * The way out. One sign-in covers e-filing and the portal, so signing out has to end both:
 * the browser is handed to the portal's sign-out page, which clears its session and lands on
 * the login page. With no portal configured this app's own sign-in page is the way out.
 */
function signOut(navigate: ReturnType<typeof useNavigate>): void {
  announceSessionEnded();
  void authStore.signOut().then(() => {
    leaveAfterSignOut(() => void navigate({ to: '/sign-in' }));
  });
}

/**
 * Where you are, which year you are in, and which IRS you are talking to.
 *
 * Nothing about who you are: the portal keeps identity, appearance and the way out at the
 * foot of the menu, in one place, and an avatar in the corner as well is the same door drawn
 * twice. The views of a screen moved down there too, under the screen they belong to.
 */
function Header({
  crumbs,
  mode,
  customerName,
  navOpen,
  onToggleNav,
}: {
  crumbs: Crumb[];
  mode?: 'ats' | 'live';
  customerName?: string;
  navOpen: boolean;
  onToggleNav: () => void;
}) {
  return (
    <header
      className={cn(
        'sticky top-0 z-20 flex h-16 shrink-0 items-center gap-4 bg-surface px-6',
        'border-b border-[color:var(--ts-border-soft)] shadow-[var(--ts-shadow-header)]',
      )}
    >
      <button
        type="button"
        onClick={onToggleNav}
        aria-label={navOpen ? 'Hide the menu' : 'Show the menu'}
        aria-expanded={navOpen}
        className={cn(
          'flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg text-primary lg:hidden',
          'transition-colors duration-150 ease-out hover:bg-[color:var(--ts-row-hover)]',
        )}
      >
        <MenuIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
      </button>

      <Breadcrumb crumbs={crumbs} />

      <div className="ml-auto flex shrink-0 items-center gap-3">
        {customerName ? (
          <span className="hidden max-w-[220px] truncate text-base font-medium text-gray-3 lg:inline">
            {customerName}
          </span>
        ) : null}
        <TaxYearPicker />
        {mode ? <ModeBadge mode={mode} /> : null}
        <UploadsTray />
      </div>
    </header>
  );
}

function Breadcrumb({ crumbs }: { crumbs: Crumb[] }) {
  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-2">
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;
        if (isLast) {
          // Keyed so a new page title fades in rather than swapping under the eye.
          return (
            <span key={`${crumb.label}-last`} className="anim-title-in truncate text-[18px] font-semibold text-primary">
              {crumb.label}
            </span>
          );
        }
        return (
          <Fragment key={`${crumb.label}-${index}`}>
            {crumb.to ? (
              <Link
                to={crumb.to}
                className="truncate text-base text-muted-foreground transition-colors duration-150 ease-out hover:text-primary"
              >
                {crumb.label}
              </Link>
            ) : (
              <span className="truncate text-base text-muted-foreground">{crumb.label}</span>
            )}
            <span aria-hidden="true" className="text-gray-1">
              /
            </span>
          </Fragment>
        );
      })}
    </nav>
  );
}

/** The one tax year the whole app works in. It survives a reload. */
function TaxYearPicker() {
  const { taxYear, setTaxYear, years, locked } = useTaxYear();
  // The newest year Tab Service files. Everything else is a year somebody has deliberately
  // gone back to, and a page of last year's e-files looks exactly like a page of this year's
  // - which is how an hour goes missing.
  const current = years.length > 0 ? Math.max(...years) : taxYear;
  const isCurrent = taxYear === current;

  const picker = (
    <RadixSelect.Root
      value={String(taxYear)}
      onValueChange={(value) => setTaxYear(Number(value))}
      disabled={locked}
    >
      <RadixSelect.Trigger
        data-tour="tax-year"
        aria-label="Tax year"
        className={cn(
          'group inline-flex h-9 cursor-pointer items-stretch overflow-hidden rounded-lg',
          'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-70',
          'border border-[color:var(--ts-input)] bg-surface',
          'transition-[border-color,box-shadow] duration-150 ease-out',
          'hover:border-[color:var(--ts-input-hover)]',
          'data-[state=open]:border-primary data-[state=open]:shadow-[0_0_0_3px_var(--ts-focus-ring-soft)]',
          'focus:border-primary focus:shadow-[0_0_0_3px_var(--ts-focus-ring-soft)] focus:outline-none',
        )}
      >
        {/*
          One control rather than three things in a row. The words "Tax Year" are the label
          of the box they sit in, on their own quiet ground; the year, whether it is the
          current one, and the way to change it are the box itself.
        */}
        <span
          className={cn(
            'hidden items-center border-r border-[color:var(--ts-border-soft)] px-3 sm:inline-flex',
            'bg-[color:var(--ts-thead-bg)] text-sm whitespace-nowrap text-gray-2',
          )}
        >
          Tax Year
        </span>
        <span className="inline-flex items-center gap-2 px-3">
          <span className="text-base font-medium text-gray-3 tabular-nums">
            <RadixSelect.Value />
          </span>
          <span
            className={cn(
              'text-[10px] font-semibold tracking-wide uppercase',
              isCurrent ? 'text-primary' : 'text-[color:var(--ts-pill-warning-text)]',
            )}
          >
            {isCurrent ? 'Current' : 'Past year'}
          </span>
        </span>
        <RadixSelect.Icon className="inline-flex items-center border-l border-[color:var(--ts-border-soft)] px-2">
          <ChevronDown
            className="h-4 w-4 text-primary transition-transform duration-150 ease-out group-data-[state=open]:rotate-180"
            aria-hidden="true"
          />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          align="end"
          sideOffset={4}
          className={cn(
            // As wide as the control it drops from. A menu narrower than its own trigger
            // reads as a second, unrelated thing that happens to have appeared underneath.
            'z-50 max-h-[320px] w-[var(--radix-select-trigger-width)] overflow-hidden rounded-lg',
            'border border-[color:var(--ts-border-soft)] bg-surface shadow-float',
            'data-[state=open]:anim-pop-in data-[state=closed]:anim-pop-out',
          )}
        >
          <RadixSelect.Viewport className="p-1">
            {years.map((year) => (
              <RadixSelect.Item
                key={year}
                value={String(year)}
                className={cn(
                  'flex h-9 cursor-pointer items-center justify-between gap-6 rounded-md px-2.5 text-base text-gray-3',
                  'outline-none transition-colors duration-150 ease-out',
                  'data-[highlighted]:bg-[color:var(--ts-row-hover)] data-[highlighted]:text-primary',
                )}
              >
                <span className="flex items-center gap-2">
                  <RadixSelect.ItemText>{year}</RadixSelect.ItemText>
                  {year === current ? (
                    <span className="text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">
                      Current
                    </span>
                  ) : null}
                </span>
                <RadixSelect.ItemIndicator>
                  <Check className="h-4 w-4 text-primary" aria-hidden="true" />
                </RadixSelect.ItemIndicator>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );

  if (!locked) return picker;
  // An e-file whose file is already on Tab Service is open, and it was checked against this
  // year. The wrapper carries the explanation because a disabled button gets no hover.
  return (
    <Tooltip content={`This e-file is for tax year ${taxYear}. To file for another year, start a new e-file.`} side="bottom">
      <span tabIndex={0} data-testid="tax-year-locked" className="inline-flex rounded-lg">
        {picker}
      </span>
    </Tooltip>
  );
}

/**
 * IRS test or live, with a 6px dot in front: hollow navy while you are in the
 * test system, filled amber once e-files are going to the IRS for real.
 */
function ModeBadge({ mode }: { mode: 'ats' | 'live' }) {
  const copy = modeCopy[mode];
  const live = mode === 'live';
  return (
    <span data-tour="mode" className="hidden sm:inline-flex">
      <Pill tone={copy.tone} title={copy.hint} className="gap-1.5 py-1">
        <span
          aria-hidden="true"
          className={cn(
            'h-1.5 w-1.5 shrink-0 rounded-full border',
            live
              ? 'border-[color:var(--ts-pill-warning-text)] bg-[color:var(--ts-pill-warning-text)]'
              : 'border-primary bg-transparent',
          )}
        />
        {copy.label} mode
      </Pill>
    </span>
  );
}
